/**
 * اپِ ادمینِ فنی — `admin.outami.com`.
 *
 * مخاطبش یک نفر است: کسی که می‌خواهد بداند اپ و کرالر دارند چه می‌کنند و
 * اگر چیزی خراب است، از کجا. پس همه‌چیزِ این اپ به نفعِ **چگالیِ اطلاعات**
 * چیده شده، نه زیبایی: جدولِ فشرده، عددِ درشت، و نمونه‌ی واقعی کنارِ هر آمار.
 *
 * ═══ چرا اپِ چهارم و نه یک مسیر در apps/web ═══
 *
 * دو دلیل، هر دو در plan/tasks.md (T08) ثبت شده‌اند:
 *   • بسته‌ی کاربرِ عادی نباید کدِ ادمین را حمل کند.
 *   • زیردامنه‌ی جدا یعنی پروژه‌ی Pagesِ جدا، که خودش یک مرزِ استقرار است.
 *
 * ═══ امنیت ═══
 *
 * پنهان کردنِ رابط، امنیت نیست و این‌جا هم نیست: هر داده‌ای که این اپ
 * می‌خواند از یک RPCِ `security definer` می‌آید که **خودش** ادمین بودن را
 * چک می‌کند (مایگریشن 0068). گاردِ پایین فقط برای این است که کاربرِ غیرادمین
 * صفحه‌ی پر از خطا نبیند.
 */
import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from '@/core/session';
import { supabase } from '@/core/lib/supabase';
import { trustThisDevice } from '@/core/lib/trustedDevice';
import { ThemeGate } from './ThemeGate';
import { useUrlState } from './useUrlState';
import { CrawlerTab } from '@/features/crawler/CrawlerTab';
import { PublishSyncTab } from '@/features/publish/PublishSyncTab';
import { SourcesTab } from '@/features/sources/SourcesTab';
import { RegistryTab } from '@/features/registry/RegistryTab';
import { ActivityTab } from '@/features/activity/ActivityTab';
import { ReviewTab } from '@/features/review/ReviewTab';
import { TagsTab } from '@/features/tags/TagsTab';
import { CompetitorsTab } from '@/features/competitors/CompetitorsTab';
import { FeedbackTab } from '@/features/feedback/FeedbackTab';
import { ReportsTab } from '@/features/reports/ReportsTab';
import { EventIssuesTab } from '@/features/eventIssues/EventIssuesTab';
import { PhotosTab } from '@/features/photos/PhotosTab';
import { AddressesTab } from '@/features/addresses/AddressesTab';
import { PromptsTab } from '@/features/prompts/PromptsTab';
import { UsersTab } from '@/features/users/UsersTab';
import { HealthTab } from '@/features/health/HealthTab';
import { OutOfBagTab } from '@/features/outofbag/OutOfBagTab';
import { RankTab } from '@/features/rank/RankTab';
import { QaTab } from '@/features/qa/QaTab';
import { QaViewTab } from '@/features/qaview/QaViewTab';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      // داده‌ی عملیاتی زود کهنه می‌شود، ولی این صفحه هم دائم باز می‌ماند.
      // نیم دقیقه یعنی «تازه است» بدونِ اینکه هر تعویضِ تب یک کوئریِ سنگین بزند.
      staleTime: 30_000,
      retry: 1,
    },
  },
});

type Tab = 'health' | 'crawler' | 'publish' | 'outofbag' | 'sources' | 'prompts' | 'registry' | 'review'
  | 'photos' | 'addresses' | 'rank' | 'tags' | 'competitors' | 'users' | 'activity' | 'feedback' | 'reports' | 'issues' | 'qa' | 'qaview';

/**
 * «سلامت» اول می‌نشیند و صفحه‌ی پیش‌فرض است (WHOSUP-264): سوالِ اولِ هر
 * بار باز کردنِ ادمین «چیزی خراب است؟» است، و این تنها سربرگی است که به
 * همان جواب می‌دهد — بی جزئیات، با چند عقربه و پیکانِ «نسبت به قبل». بقیه‌ی
 * سربرگ‌ها جوابِ سوالِ دوم‌اند: «کجا و چرا».
 *
 * «کرالر» و «منابع» عمداً دو سربرگ‌اند و یکی نشدند: آن یکی **شمارش** است
 * («این هفته کجا ریخت») و این یکی **ردیف** («این سایت را قبلاً دیده‌ایم و
 * چه شد»). دو سوالِ متفاوت با دو ریتمِ متفاوت — یکی را روزانه نگاه می‌کنی،
 * آن یکی را وقتی می‌خواهی شهرِ تازه‌ای را باز کنی.
 *
 * «کرالر ↔ اپ» بلافاصله بعدِ «کرالر» می‌آید و همین ترتیب حرفش را می‌زند:
 * آن یکی می‌گوید خطِ لوله چه ساخت، این یکی می‌گوید از آن ساخته‌ها چه چیزی
 * واقعاً به دستِ کاربر رسید. اعدادش تک‌اند و نه جفت، چون سوالش «الان» است
 * و نه «در چه بازه‌ای».
 *
 * «پرامپت‌ها» بلافاصله بعدِ «منابع» می‌نشیند و نه کنارِ «کرالر» (WHOSUP-215):
 * دو کاری که مدل در این خط می‌کند — کشفِ منبع و غربالگریِ پیش‌نویس — هر دو
 * ورودیِ همان سربرگِ «منابع»اند، و کسی که آن‌جا می‌پرسد «این پیش‌نویس از
 * کجا آمد و چرا رد شد»، جوابش یک سربرگ آن‌طرف‌تر است. «کرالر» درباره‌ی
 * **شمارشِ** خطِ لوله است و مدل فقط یکی از ورودی‌هایش.
 *
 * و «کارنامه» درست بعدِ «منابع» می‌نشیند چون سوالِ بعدیِ همان است: آن یکی
 * می‌گوید این سایت را می‌شناسیم و آخرین بار چه شد، این یکی می‌گوید آن ردیف
 * **چقدر می‌ارزد** — یعنی با حذفش چه از دست می‌رود.
 *
 * «عکس‌ها» درست بعدِ «صفِ بازبینی» می‌نشیند چون همان سوال است، باریک‌شده:
 * بازبینی می‌گوید کدام آیتم‌ها به چشمِ آدم مشکل دارند، این یکی همان را فقط
 * برای عکس‌ها و با شمارش جواب می‌دهد — تکراری‌ها، و آن‌هایی که روی دستگاهِ
 * کاربر اصلاً لود نشدند.
 *
 * و «آدرس‌ها» بلافاصله بعدِ عکس‌ها، چون خواهرِ تنیِ همان است (WHOSUP-201):
 * باز هم یک فیلدِ واحد، باز هم فیلدی که کرالر از سایتِ غریبه کنده و هیچ
 * تستی نگاهش نمی‌کند. جفتشان «کیفیتِ یک ستون»اند و نه «صفِ کار» — و این
 * ترتیب همین را می‌گوید.
 *
 * «کاربران» درست **قبلِ** «فعالیتِ کاربران» می‌نشیند (WHOSUP-243): آن یکی
 * تجمیع است («ده روزِ گذشته ۱۲ نفر آمدند») و این یکی ردیف («کدام ۱۲ نفر»).
 * ترتیبش هم همین را می‌گوید: اول می‌بینی چه کسانی هستند، بعد می‌پرسی روی
 * هم چه کردند. و علامتِ «تستی» هم این‌جاست، چون همان‌جایی است که آدم‌ها را
 * یکی‌یکی می‌بینی — یعنی همان‌جایی که می‌فهمی کدامشان واقعی نیست.
 *
 * «بازخوردها» ته می‌نشیند و بعدِ «فعالیتِ کاربران» — چون همان سوال را یک
 * پله جلوتر می‌برد: آن یکی می‌گوید آدم‌ها **چه کردند**، این یکی می‌گوید
 * خودشان **چه گفتند**.
 *
 * و «گزارش‌ها» درست بعدِ آن (WHOSUP-168): هم‌جنسِ همان است — محتوایش را آدم
 * نوشته و نه ماشین — ولی جنسِ کارش فرق دارد. بازخورد خبر است و خواندنش
 * کافی است؛ گزارش یک **صف** است و هر ردیفش منتظرِ یک تصمیم می‌ماند. برای
 * همین آخر می‌نشیند و نه قاطیِ بازخوردها.
 *
 * ⚠ از مایگریشنِ ۰۱۰۸ هیچ سربرگی انتخابگرِ پنجره یا انتخابگرِ اجرا ندارد:
 * هر عدد خودش دو تاست — ۱۰ روزِ اخیر و آخرین اجرا. شکل و دلیلش در
 * `ui/Num.tsx`.
 */
const TABS: { id: Tab; label: string }[] = [
  { id: 'health', label: 'سلامت' },
  { id: 'crawler', label: 'خطِ لوله‌ی کرالر' },
  { id: 'publish', label: 'کرالر ↔ اپ' },
  // «بیرون از پاکت» (WHOSUP-286) کنارِ «کرالر ↔ اپ»: همان سوالِ «چه به کاربر
  // رسید»، از طرفِ دیگرش — چه چیزی عمداً نرسید و جای دیگری هم نبود.
  { id: 'outofbag', label: 'بیرون از پاکت' },
  { id: 'sources', label: 'منابع' },
  { id: 'prompts', label: 'پرامپت‌ها' },
  { id: 'registry', label: 'کارنامه‌ی رجیستری' },
  { id: 'review', label: 'صفِ بازبینی' },
  { id: 'photos', label: 'عکس‌ها' },
  { id: 'addresses', label: 'آدرس‌ها' },
  // «ترتیبِ پیشنهادی» (WHOSUP-285) بعدِ عکس‌ها و آدرس‌ها می‌نشیند چون
  // همان ستون‌ها را مصرف می‌کند: آن دو می‌گویند یک فیلد چقدر سالم است، این
  // یکی می‌گوید آن فیلد و رفتارِ کاربران روی هم رویدادها را چطور از هم جدا
  // می‌کنند — قدمِ اولِ فرمولِ ترتیب.
  { id: 'rank', label: 'ترتیبِ پیشنهادی' },
  { id: 'tags', label: 'سلسله‌مراتبِ تگ‌ها' },
  // «سایت‌های رقیب» کنارِ تگ‌ها می‌نشیند چون هم‌جنسِ همان است: پیکربندیِ
  // رفتارِ خودِ اپ (استثنای utm روی لینک‌های خروجی — مایگریشن 0134)، نه
  // گزارشِ چیزی که ماشین ساخته.
  { id: 'competitors', label: 'سایت‌های رقیب' },
  { id: 'users', label: 'کاربران' },
  { id: 'activity', label: 'فعالیتِ کاربران' },
  { id: 'feedback', label: 'بازخوردها' },
  { id: 'reports', label: 'گزارش‌ها' },
  // «پرچم‌های رخداد» (مایگریشن 0200) کنارِ «گزارش‌ها»: هم‌جنسِ همان صف است —
  // حرفِ کاربر که منتظرِ تصمیم می‌ماند — ولی درباره‌ی داده‌ی رخداد، نه آدمِ دیگر.
  { id: 'issues', label: 'پرچم‌های رخداد' },
  // «تستِ دستی» آخر است: ابزارِ کار با تستر، نه گزارشِ اپ. داده‌اش فقط روی
  // develop است (مایگریشن 0161)، پس روی admin.whosup.ca خالی می‌ماند.
  { id: 'qa', label: 'تستِ دستی' },
  // کنارِ «تستِ دستی»: همان کار، از چشمِ خودِ تستر (فقط‌نمایش).
  { id: 'qaview', label: 'شبیه‌سازِ تستر' },
];

function Login({ onTrustFailed }: { onTrustFailed: (error: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * «به این دستگاه اعتماد دارم» (WHOSUP-349): نشست همین حالا هم روی دستگاه
   * می‌ماند؛ چیزی که این تیک عوض می‌کند این است که «خروج» روی گوشی یا هر
   * دستگاهِ دیگری دیگر این‌جا را نمی‌بندد. نگاه کن به lib/trustedDevice.
   *
   * شکستش را `Shell` نشان می‌دهد، نه همین فرم (WHOSUP-374): با ورودِ موفق این
   * فرم همان لحظه کنار می‌رود و پیامی که این‌جا بنشیند هرگز دیده نمی‌شود.
   */
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) setError(err.message);
    else if (trust) {
      const r = await trustThisDevice();
      if (!r.ok) onTrustFailed(r.error ?? '');
    }
    setBusy(false);
  };

  return (
    <form className="login card" onSubmit={submit}>
      <h1>Pals & Plans · admin</h1>
      <p className="dim" style={{ marginBottom: 12 }}>ابزارِ داخلی.</p>
      <input type="email" placeholder="ایمیل" value={email} autoComplete="username"
        onChange={(e) => setEmail(e.target.value)} />
      <input type="password" placeholder="رمز" value={password} autoComplete="current-password"
        onChange={(e) => setPassword(e.target.value)} />
      <label className="trust">
        <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} />
        به این دستگاه اعتماد دارم — دیگر ورود نخواه
      </label>
      {error ? <p className="err" style={{ marginBottom: 8 }}>{error}</p> : null}
      <button className="btn block" type="submit" disabled={busy || !email || !password}>
        {busy ? '…' : 'ورود'}
      </button>
    </form>
  );
}

function Shell() {
  // `signOut` و نه `supabase.auth.signOut()`ِ خام: آن یکی سراسری است و
  // دستگاه‌های مورد اعتماد را هم می‌بست (WHOSUP-349، lib/authSignOut).
  const { session, profile, loading, signOut } = useSession();
  // سربرگ در آدرس می‌نشیند تا رفرش نپراندش — و تا بشود لینکش را فرستاد.
  const [tab, setTab] = useUrlState('tab', 'health', TABS.map((t) => t.id));
  // ورود گرفت ولی «اعتماد» نه (WHOSUP-374) — تا وقتی درست نشده جلوی چشم می‌ماند.
  const [trustError, setTrustError] = useState<string | null>(null);
  const retryTrust = async () => {
    const r = await trustThisDevice();
    setTrustError(r.ok ? null : (r.error ?? ''));
  };

  if (loading) return <div className="wrap"><p className="dim">…</p></div>;
  if (!session) return <div className="wrap"><Login onTrustFailed={setTrustError} /></div>;

  const trustBanner = trustError !== null ? (
    <div className="card" style={{ marginBottom: 12 }}>
      <p className="err" style={{ marginBottom: 8 }}>
        وارد شدی، ولی «به این دستگاه اعتماد دارم» ننشست: «خروج» روی گوشی یا
        دستگاهِ دیگر هنوز این‌جا را هم می‌بندد.
        {trustError ? <span className="dim"> ({trustError})</span> : null}
      </p>
      <button className="btn" type="button" onClick={() => void retryTrust()}>دوباره امتحان کن</button>
    </div>
  ) : null;

  // پروفایل ممکن است یک تیک دیرتر برسد؛ تا آن موقع «دسترسی نداری» نگوییم.
  if (profile && !profile.isAdmin) {
    return (
      <div className="wrap">
        <div className="card">
          <h1>دسترسی نداری</h1>
          <p className="dim">این حساب ادمین نیست.</p>
          <button className="btn" type="button" style={{ marginTop: 10 }}
            onClick={() => void signOut()}>خروج</button>
        </div>
      </div>
    );
  }

  return (
    <div className="wrap">
      <div className="topbar">
        <h1>Pals & Plans · admin</h1>
        <nav className="tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={`tab${tab === t.id ? ' on' : ''}`}
              onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </nav>
        <span className="spacer" />
        <button className="tab" type="button"
          onClick={() => void signOut()}>خروج</button>
      </div>

      {trustBanner}
      {tab === 'health' ? <HealthTab /> : null}
      {tab === 'crawler' ? <CrawlerTab /> : null}
      {tab === 'publish' ? <PublishSyncTab /> : null}
      {tab === 'outofbag' ? <OutOfBagTab /> : null}
      {tab === 'sources' ? <SourcesTab /> : null}
      {tab === 'prompts' ? <PromptsTab /> : null}
      {tab === 'registry' ? <RegistryTab /> : null}
      {tab === 'review' ? <ReviewTab /> : null}
      {tab === 'photos' ? <PhotosTab /> : null}
      {tab === 'addresses' ? <AddressesTab /> : null}
      {tab === 'rank' ? <RankTab /> : null}
      {tab === 'tags' ? <TagsTab /> : null}
      {tab === 'competitors' ? <CompetitorsTab /> : null}
      {tab === 'users' ? <UsersTab /> : null}
      {tab === 'activity' ? <ActivityTab /> : null}
      {tab === 'feedback' ? <FeedbackTab /> : null}
      {tab === 'reports' ? <ReportsTab /> : null}
      {tab === 'issues' ? <EventIssuesTab /> : null}
      {tab === 'qa' ? <QaTab /> : null}
      {tab === 'qaview' ? <QaViewTab /> : null}
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <ThemeGate>
        <SessionProvider>
          <Shell />
        </SessionProvider>
      </ThemeGate>
    </QueryClientProvider>
  );
}
